import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { AgentService } from '@services/agent.service';
import gte from 'semver/functions/gte';
import { SessionContext } from '@contexts/session-context';

const VERSION_THRESHOLD = '2.0.0';

const getDrifterVersion = async (
  userId: number,
): Promise<string | undefined> => {
  try {
    const systemInfo = await AgentService.ping();
    if (systemInfo?.uniqueId) {
      const agentInfo = await AgentService.findByUuid(systemInfo.uniqueId);
      if (agentInfo?.drifterVersion) {
        return agentInfo.drifterVersion;
      }
    }
  } catch (error) {
    // Silently fall back to fetching user's agents
    // eslint-disable-next-line no-console
    console.error(
      "No Agent is running in the system fallback to fetching user's agents:",
      error,
    );
  }

  try {
    const allAgentsForUser = await AgentService.findAll(
      `isObsolete:false,agentType!TRANSIENT,createdById:${userId}`,
      'updatedAtEpoch,desc',
      10,
      0,
    );
    return allAgentsForUser?.content?.[0]?.drifterVersion;
  } catch (error) {
    return undefined;
  }
};

export const useDrifterVersionCheck = () => {
  const { session } = useContext(SessionContext);
  const userId = session?.user?.id;

  const { data: drifterVersion, isLoading } = useQuery({
    queryKey: ['drifter-version-check', userId],
    queryFn: () => getDrifterVersion(userId!),
    enabled: !!userId,
    retry: false,
    onError: () => {
      // Fail silently - hide banner on API error
    },
  });

  // Check if drifter version is below threshold using semver
  const hasOldDrifterVersion = useMemo(() => {
    if (!drifterVersion) {
      return false;
    }
    try {
      // gte returns true when version >= threshold
      return !gte(drifterVersion, VERSION_THRESHOLD);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to parse drifter version with semver:',
        drifterVersion,
        error,
      );
      return false;
    }
  }, [drifterVersion]);

  return {
    hasOldDrifterVersion,
    isLoading,
  };
};
